/**
 * useDragReorder — Drag-and-drop reorder logic for Input and Output sections.
 * Phase 3B: uses queryClient.setQueryData for optimistic cache updates
 * instead of direct state mutation + reloadState.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../../../services/ApiService';
import { SCENARIO_TREE_KEY, type EditorState, patchTreeCache } from '../../../shared/hooks/queries/useScenarioTree';

interface UseDragReorderParams {
    scenarioId: number;
}

const reorder = (list: any[], startIndex: number, endIndex: number): Array<any> => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

export function useDragReorder({ scenarioId }: UseDragReorderParams) {
    const queryClient = useQueryClient();

    const patchTree = useCallback(
        (updater: (prev: EditorState) => EditorState) =>
            patchTreeCache(queryClient, scenarioId, updater),
        [queryClient, scenarioId]
    );

    const onDragEndInput = useCallback((result: any) => {
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (!currentState) return;
        const { tree } = currentState.Input;

        if (!result.destination) return;

        const movedItem = tree.items[result.draggableId];
        if (!movedItem) return;
        const movedItemPosition = tree.items[movedItem.parentId]?.childrens?.indexOf(result.draggableId);
        if (result.destination.index === movedItemPosition) return;

        apiService.putModelData(movedItem.id, movedItem.name, result.destination.index);

        patchTree((prev) => {
            const parentItem = prev.Input.tree.items[movedItem.parentId];
            if (!parentItem) return prev;
            return {
                ...prev,
                isSync: false,
                Input: {
                    ...prev.Input,
                    tree: {
                        ...prev.Input.tree,
                        items: {
                            ...prev.Input.tree.items,
                            [movedItem.parentId]: {
                                ...parentItem,
                                childrens: reorder(
                                    parentItem.childrens,
                                    result.source.index,
                                    result.destination.index
                                ),
                            },
                        },
                    },
                },
            };
        });
    }, [queryClient, scenarioId, patchTree]);

    const onDragEndOutput = useCallback((result: any) => {
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (!currentState) return;
        const { tree } = currentState.Output;

        if (!result.destination) return;

        const movedItem = tree.items[result.draggableId];
        if (!movedItem) return;
        const movedItemPosition = tree.items[movedItem.parentId]?.childrens?.indexOf(result.draggableId);
        if (result.destination.index === movedItemPosition) return;

        apiService.putOutputModelData(movedItem.id, result.destination.index);

        patchTree((prev) => {
            const parentItem = prev.Output.tree.items[movedItem.parentId];
            if (!parentItem) return prev;
            return {
                ...prev,
                isSync: false,
                Output: {
                    ...prev.Output,
                    tree: {
                        ...prev.Output.tree,
                        items: {
                            ...prev.Output.tree.items,
                            [movedItem.parentId]: {
                                ...parentItem,
                                childrens: reorder(
                                    parentItem.childrens,
                                    result.source.index,
                                    result.destination.index
                                ),
                            },
                        },
                    },
                },
            };
        });
    }, [queryClient, scenarioId, patchTree]);

    const handleSectionDragEnd = useCallback((result: any, variant: 'Input' | 'Output') => {
        if (variant === 'Input') {
            onDragEndInput(result);
        } else {
            onDragEndOutput(result);
        }
    }, [onDragEndInput, onDragEndOutput]);

    return { handleSectionDragEnd };
}
